import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DatePicker, Form, Input } from 'antd';
import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';
const { RangePicker } = DatePicker;
import dayjs from 'dayjs';
import mapboxgl from 'mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { mapBoxId, mapBoxStyles, MapLat, MapLng } from '../../constants/keys';
import { DateFormat, InitialEditLocationFields } from '../../constants/data';

import styles from './styles.module.scss';
import {
  sensorsApi,
  useCreateLocationMutation,
  useEditLocationMutation,
} from '../../store/api/sensors';
import { useGetBoundariesIdQuery, useGetBoundariesQuery, useGetFieldsQuery } from '../../store/api/fields';
import { ActiveBorderPolygon, DefaultBgPolygon } from '../../constants/ui';
import { useLocalStorage } from '../../hooks';
import { selectBoundary } from '../../store/slices/sensors';

mapboxgl.accessToken = mapBoxId;

export const AddPeriodForm = ({
  t,
  sensorId,
  initialData = InitialEditLocationFields,
  disabledPeriods = [],
  closePanel
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [period, setPeriod] = useState({ sowingDate: initialData.install, harvestDate: initialData.removal });
  const [mapLng, setMapLng] = useLocalStorage(MapLng, '');
  const [mapLat, setMapLat] = useLocalStorage(MapLat, '');
  const [lng, setLng] = useState(initialData.coordinates.length ? initialData.coordinates[0] : mapLng || 21.3275);
  const [lat, setLat] = useState(initialData.coordinates.length ? initialData.coordinates[1] : mapLat || 52.2854);
  // const [lng, setLng] = useState(initialData.coordinates.length ? initialData.coordinates[0] : mapLng || 21.3275);
  // const [lat, setLat] = useState(initialData.coordinates.length ? initialData.coordinates[1] : mapLat || 52.2854);
  const [zoom, setZoom] = useState(14);
  const [sourceSeasons, setSourceSeasons] = useState([]);
  const [styleMapLoaded, setStyleMapLoaded] = useState(false);

  // const inputRef = useRef(null);
  const mapContainer = useRef(null);
  const mapMarker = useRef(null);
  const map = useRef(null);

  const { boundaryId } = useSelector((state) => state.sensors);

  const [createPeriod, { isSuccess: isCreatePeriodSuccess }] = useCreateLocationMutation();
  const [editPeriod, { isSuccess: isEditPeriodSuccess }] = useEditLocationMutation();

  const { data: boundaries, isSuccess: isBoundariesSuccess } = useGetBoundariesQuery({
    harvest_date__gte: period.harvestDate,
    sowing_date__lte: period.sowingDate,
    has_field_season: 1,
  });

  const { data: fields, isSuccess: isFieldsSuccess } = useGetFieldsQuery();

  const { data: currentBoundary, isSuccess: isCurrentBoundarySuccess } = useGetBoundariesIdQuery(
    { id: boundaryId },
    { skip: !boundaryId, refetchOnMountOrArgChange: true },
  );

  // Add
  const onAddPeriod = () => {
    if (values.range && values.name) {
      createPeriod({
        sensorId,
        name: values.name,
        installPoint: {
          type: 'Point',
          coordinates: [lng, lat],
        },
        installDate: dayjs(values.range[0]).format(DateFormat),
        removalDate: dayjs(values.range[1]).format(DateFormat),
      });
    }
  };

  const disabledDate = (current) => {
    let disabledPeriod;
    disabledPeriods.map((period) => {
      disabledPeriod =
        disabledPeriod || (current > dayjs(period[0]).endOf('day') && current < dayjs(period[1]).endOf('day'));
    });
    return disabledPeriod;
  };

  // Edit
  const onEditPeriod = () => {
    if (initialData.locationId && values.range && values.name) {
      editPeriod({
        locationId: initialData.locationId,
        sensorId: sensorId,
        name: values.name,
        installPoint: {
          type: 'Point',
          coordinates: [lng, lat],
        },
        installDate: dayjs(values.range[0]).format(DateFormat),
        removalDate: dayjs(values.range[1]).format(DateFormat),
      });
    }
  };

  const removePolygonSources = (id, layerFillId, layerOutlineId, currentMap) => {
    const source = currentMap.getSource(id);
    if (source) {
      currentMap.removeLayer(layerFillId);
      currentMap.removeLayer(layerOutlineId);
      currentMap.removeSource(id);
    }
  };

  const onChangeRange = (value) => {
    setPeriod({ sowingDate: dayjs(value[0]).format(DateFormat), harvestDate: dayjs(value[1]).format(DateFormat) });
  };

  const goToFields = () => {
    navigate('/fields');
  };

  // initialize map only once
  useEffect(() => {
    setStyleMapLoaded(false);

    if (sourceSeasons.length) {
      sourceSeasons.forEach((source) => {
        removePolygonSources(source.id, source.layerFillId, source.layerOutlineId, map.current);
      });
      setSourceSeasons([]);
    }

    form.setFieldsValue({
      range: initialData.install && initialData.removal ? [dayjs(initialData.install), dayjs(initialData.removal)] : null,
      name: initialData.name,
    });

    if (map.current) return;

    setTimeout(() => {
      let flying = false;

      if (map.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapBoxStyles,
        center: [lng, lat],
        zoom: zoom,
      });

      mapMarker.current = new mapboxgl.Marker({ element: mapMarker.current, draggable: true })
        .setLngLat([lng, lat])
        .addTo(map.current);

      // if (!initialData.coordinates || initialData.coordinates.length === 0) {
      //   if ('geolocation' in navigator) {
      //     navigator.geolocation.getCurrentPosition((position) => {
      //       const currentLat = position.coords.latitude;
      //       const currentLng = position.coords.longitude;
      //       setLng(currentLng);
      //       setLat(currentLat);
      //       setMapLat(currentLat);
      //       setMapLng(currentLng);
      //       // mapMarker.current.setLngLat([currentLng, currentLat]);
      //       map.current.flyTo({
      //         center: [currentLng, currentLat],
      //       });
      //       map.current.fire('flystart');
      //       flying = true;
      //     });
      //   } else {
      //     console.log('Geolocation is not available');
      //   }
      // }

      map.current.on('flystart', () => {
        flying = true;
      });

      map.current.on('flyend', () => {
        flying = false;
      });

      map.current.on('moveend', () => {
        if (flying) {
          map.current.fire('flyend');
        }
        // setLng(map.current.getCenter().lng);
        // setLat(map.current.getCenter().lat);
        // setMapLat(map.current.getCenter().lat);
        // setMapLng(map.current.getCenter().lng);
        // setZoom(map.current.getZoom().toFixed(2));
      });

      // map.current.on('drag', function () {
      //   if (flying) return;
      //   const centerPosition = map.current.getCenter();
      //   marker.setLngLat(centerPosition);
      // });

      // map.current.on('zoom', function () {
      //   const centerPosition = map.current.getCenter();
      //   marker.setLngLat(centerPosition);
      // });

      mapMarker.current.on('dragend', () => {
        if (flying) return;
        const { lng, lat } = mapMarker.current.getLngLat();
        setLng(lng);
        setLat(lat);
        setMapLat(lat);
        setMapLng(lng);
      });

      map.current.on('dblclick', (e) => {
        if (flying) return;
        const { lng, lat } = e.lngLat
        mapMarker.current.setLngLat([lng, lat])
        setLng(lng);
        setLat(lat);
        setMapLat(lat);
        setMapLng(lng);
      })

      map.current.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          // When active the map will receive updates to the device's location as it changes.
          trackUserLocation: true,
          // Draw an arrow next to the location dot to indicate which direction the device is heading.
          showUserHeading: true,
        }),
      );

      map.current.on('style.load', () => {
        setStyleMapLoaded(true);
      });
    }, 10); // wait for mapbox to load for map width in modal
  }, []);

  useEffect(() => {
    if (isFieldsSuccess) {
      const seasons = [];
      fields.map((obj) => {
        if (obj.seasons) {
          seasons.push(...obj.seasons);
        }
      });
      const season = seasons.filter((obj) => obj.id === initialData.seasonId);
      dispatch(selectBoundary(season.length ? season[0].boundary_id : ''));
    }
  }, [isFieldsSuccess]);

  // Location was changed
  useEffect(() => {
    if (isEditPeriodSuccess) {
      dispatch(sensorsApi.util.resetApiState());
      closePanel();
    }
  }, [isEditPeriodSuccess]);

  // Location was created
  useEffect(() => {
    if (isCreatePeriodSuccess) {
      dispatch(sensorsApi.util.resetApiState());
      closePanel();
    }
  }, [isCreatePeriodSuccess]);

  // Add fields (polygons)
  useEffect(() => {
    // if (initialData.seasonId || !styleMapLoaded || !map.current || (!isBoundariesSuccess && !isCurrentBoundarySuccess))
    if (!styleMapLoaded || !map.current || (!isBoundariesSuccess && !isCurrentBoundarySuccess)) return;

    setTimeout(() => {
      if (isBoundariesSuccess) {
        if (sourceSeasons.length) {
          sourceSeasons.forEach((source) => {
            removePolygonSources(source.id, source.layerFillId, source.layerOutlineId, map.current);
          });
          setSourceSeasons([]);
        }

        const listSource = [];
        const fullListBoundaries = currentBoundary ? [currentBoundary, ...boundaries] : [...boundaries];
        fullListBoundaries.map((obj, index) => {
          // Polygon
          map.current.addSource(`polygon-${index}`, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: obj.geom.coordinates[0],
              },
            },
          });

          map.current.addLayer({
            id: `polygon-fill-${index}`,
            type: 'fill',
            source: `polygon-${index}`,
            layout: {},
            paint: {
              'fill-color': DefaultBgPolygon,
              'fill-opacity': 0.4,
            },
          });

          map.current.addLayer({
            id: `polygon-outline-${index}`,
            type: 'line',
            source: `polygon-${index}`,
            layout: {},
            paint: {
              'line-color': ActiveBorderPolygon,
              'line-width': 2,
            },
          });

          listSource.push({
            id: `polygon-${index}`,
            layerFillId: `polygon-fill-${index}`,
            layerOutlineId: `polygon-outline-${index}`,
          });
        });
        setSourceSeasons(listSource);

        if (listSource.length) {
          const bounds = new mapboxgl.LngLatBounds();
          fullListBoundaries[0].geom.coordinates[0][0].forEach((coord) => {
            bounds.extend(coord);
          });

          map.current.fitBounds(bounds, {
            zoom: zoom,
          });

          // Choose the period by season
          // if (!isRangeChanged) {
          //   const seasons = fields.flatMap((item) => item.seasons || []);
          //   const activeSeason = seasons.find((s) => s.boundary_id === fullListBoundaries[0].id);
          //   if (activeSeason) {
          //     const { sowing_date, harvest_date } = activeSeason;
          //     form.setFieldValue('range', [sowing_date, harvest_date]);
          //     onChangeRange([sowing_date, harvest_date]);
          //   }
          // }
          // setIsRangeChanged(true);

          const centerPositionMarker = bounds.getCenter();

          if (!initialData.seasonId) {
            mapMarker.current.setLngLat([centerPositionMarker.lng, centerPositionMarker.lat]);
          }

          if (
            centerPositionMarker &&
            mapMarker.current &&
            initialData.coordinates.length === 0 &&
            centerPositionMarker
          ) {
            mapMarker.current.setLngLat(centerPositionMarker);
            setLng(centerPositionMarker.lng);
            setLat(centerPositionMarker.lat);
            setMapLat(centerPositionMarker.lat);
            setMapLng(centerPositionMarker.lng);
          }
        }
      }
    }, 20);
  }, [boundaries, styleMapLoaded, currentBoundary, isCurrentBoundarySuccess, isBoundariesSuccess, map]);

  return (
    <>
      <Form form={form} layout="vertical" requiredMark={false}>
        <div className={styles.formBody}>
          <div>
            {/*{!!sensorName && (*/}
            {/*  <div className={styles.messageSuccess}>*/}
            {/*    <CheckCircleIcon />*/}
            {/*    Device <strong>{sensorName}</strong> was added successfully!*/}
            {/*  </div>*/}
            {/*)}*/}
            <Form.Item
              name="range"
              label={t('locations.addPeriod.period')}
              rules={[
                {
                  required: true,
                  message: t('forms.validation.pleaseInput'),
                },
              ]}
            >
              <RangePicker
                format={DateFormat}
                disabledDate={disabledDate}
                inputReadOnly={true}
                size={'large'}
                rootClassName={'custom-date-picker custom-date-picker--range'}
                onChange={onChangeRange}
              />
            </Form.Item>
            <Form.Item label={t('locations.addPeriod.description')} name="name" rules={[{ required: true }]}>
              <Input placeholder={t('locations.addPeriod.descriptionPlaceholder')} size="large" />
            </Form.Item>
          </div>
          <p dangerouslySetInnerHTML={{ __html: t('locations.addPeriod.text') }}></p>
          <div className={styles.formMapContainer}>
            <div ref={mapContainer} className={styles.formMap} />
            <div ref={mapMarker} className={styles.formMapPin}>
              <CroplerIcon name="pin" />
            </div>
            <Button
              type="secondary"
              size={'large'}
              className={styles.formMapButton}
              icon={<CroplerIcon name="polygon" />}
              onClick={goToFields}
            >
              {t('fields.addField')}
            </Button>
          </div>
        </div>
        <div className={styles.formNav}>
          <Button size={'large'} type="secondary" onClick={() => closePanel()}>
            {t('actions.cancel')}
          </Button>
          {initialData.locationId ? (
            <Button type="primary" size={'large'} icon={<CroplerIcon name="check" />} onClick={() => onEditPeriod()} htmlType="submit">
              {t('locations.addPeriod.edit')}
            </Button>
          ) : (
            <Button type="primary" size={'large'} onClick={() => onAddPeriod()} htmlType="submit">
              {t('locations.addPeriod.set')}
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};
